import { useEffect, useRef, useState } from 'react'
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion'
import { trackWeb, WEB_EVENTS } from '../lib/analytics'
import { usePlatform } from '../lib/platform'
import { DownloadCta } from './DownloadCta'

/**
 * The landing hero's CTA row. While it is on screen the sticky bar must stay
 * hidden. A page with a different hero anchor passes its own id rather than
 * competing for this one - two pages sharing one global element id would be a
 * collision waiting to happen.
 */
const DEFAULT_HERO_CTA_ID = 'get'

interface StickyDownloadBarProps {
  /** Id of the in-page CTA the bar hides behind. Defaults to the landing's `#get`. */
  heroCtaId?: string
}

/**
 * Mobile-only bottom bar carrying the download CTA for the whole page below the
 * hero. Renders nothing on desktop, where the hero pair plus the section CTAs
 * are already always a scroll away.
 */
export function StickyDownloadBar({ heroCtaId = DEFAULT_HERO_CTA_ID }: StickyDownloadBarProps) {
  const { store, context } = usePlatform()
  const reduced = usePrefersReducedMotion()
  const [visible, setVisible] = useState(false)
  const shownReported = useRef(false)

  useEffect(() => {
    if (store === null) return
    const heroCta = document.getElementById(heroCtaId)
    if (!heroCta || !('IntersectionObserver' in window)) {
      // No anchor to hide behind (or no observer): the bar is more valuable
      // shown than withheld, and it cannot overlap a CTA it cannot find.
      setVisible(true)
      return
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => setVisible(!entry.isIntersecting))
      },
      { threshold: 0 },
    )
    io.observe(heroCta)
    return () => io.disconnect()
  }, [store, heroCtaId])

  useEffect(() => {
    if (!visible || shownReported.current) return
    shownReported.current = true
    // Segmentable by platform: the reveal rate is only meaningful against the
    // store the visitor was actually offered, and against the in-app browser
    // (or lack of one) the paid traffic arrived in.
    trackWeb(WEB_EVENTS.WEB_STICKY_CTA_SHOWN, { store, context })
  }, [visible, store, context])

  if (store === null) return null

  const classes = ['stickybar']
  if (visible) classes.push('in')
  if (reduced) classes.push('noanim')

  return (
    <>
      {/* Always present once the bar mounts, so the fixed bar never covers the
          end of the footer - and reserving it up front keeps the reveal at
          zero layout shift. */}
      <div className='stickybar-spacer' aria-hidden='true' />
      <div className={classes.join(' ')} role='region' aria-label='Get the Compass app'>
        <p className='sbline'>Get Compass - plan trips from your reels</p>
        <DownloadCta placement='sticky' className='storebtn-sm' />
      </div>
    </>
  )
}
